package co.maytes.standupbot.dashboard.components.widgets

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.motion.pressable
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Typography
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.CSSColorValue

enum class ChipTone { Neutral, Accent }

private data class ChipPaint(
    val bg: String,
    val bgSelected: String,
    val fg: String,
    val fgSelected: String,
    val ring: String,
    val ringSelected: String,
)

private fun chipPaint(tone: ChipTone): ChipPaint = when (tone) {
    ChipTone.Neutral -> ChipPaint(
        bg = Palette.surface.cssRgb(),
        bgSelected = "rgba(94, 106, 210, 0.10)",
        fg = Palette.textSecondary.cssRgb(),
        fgSelected = Palette.accentHover.cssRgb(),
        ring = Palette.border.cssRgb(),
        ringSelected = "rgba(94, 106, 210, 0.30)",
    )
    ChipTone.Accent -> ChipPaint(
        bg = "rgba(94, 106, 210, 0.08)",
        bgSelected = "rgba(94, 106, 210, 0.16)",
        fg = Palette.accentHover.cssRgb(),
        fgSelected = Palette.accentHover.cssRgb(),
        ring = "rgba(94, 106, 210, 0.22)",
        ringSelected = "rgba(94, 106, 210, 0.40)",
    )
}

@Composable
fun Chip(
    label: String,
    tone: ChipTone = ChipTone.Neutral,
    modifier: Modifier = Modifier,
    selected: Boolean = false,
    leadingDot: CSSColorValue? = null,
    leadingIcon: (@Composable () -> Unit)? = null,
    onClick: (() -> Unit)? = null,
) {
    val paint = chipPaint(tone)
    val interactive = onClick != null
    val bg = if (selected) paint.bgSelected else paint.bg
    val fg = if (selected) paint.fgSelected else paint.fg
    val ring = if (selected) paint.ringSelected else paint.ring
    val base = Modifier
        .borderRadius(Radius.full)
        .styleModifier {
            property("display", "inline-flex")
            property("align-items", "center")
            property("gap", "6px")
            property("width", "fit-content")
            property("justify-self", "start")
            property("padding", "4px 10px")
            property("background-color", bg)
            property("color", fg)
            property("box-shadow", "inset 0 0 0 1px $ring")
            property("font-feature-settings", "\"tnum\" 1")
            property("font-variant-numeric", "tabular-nums")
            property("white-space", "nowrap")
            property("user-select", "none")
            if (interactive) {
                property("cursor", "pointer")
                property("outline", "none")
            } else {
                property("transition", Motion.TRANSITION_HOVER)
            }
        }
    val classed = if (interactive) base.pressable() else base
    val clickable = if (onClick != null) classed.onClick { onClick() } else classed
    Box(modifier = clickable.then(modifier)) {
        if (leadingDot != null) {
            Box(
                modifier = Modifier
                    .borderRadius(Radius.full)
                    .styleModifier {
                        property("width", "6px")
                        property("height", "6px")
                        property("background-color", leadingDot.cssRgb())
                        property("flex-shrink", "0")
                    },
            )
        }
        if (leadingIcon != null) {
            Box(
                modifier = Modifier.styleModifier {
                    property("display", "inline-flex")
                    property("align-items", "center")
                    property("flex-shrink", "0")
                    property("margin-left", "-1px")
                },
            ) {
                leadingIcon()
            }
        }
        SpanText(
            text = label,
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .fontWeight(550),
        )
    }
}
